import type { GetStartedDomainTruth } from '@/core/guidance/use-get-started-domain';
import {
  GUIDANCE_TARGET,
  HINT_ID,
  type GuidanceTarget,
  type HintId,
} from '@/core/guidance/constants';
import { canonicalizeShellPathname, shellPaths } from '@/core/router/app-paths';

export type HintEligibilityContext = {
  domain: GetStartedDomainTruth;
  pathname: string;
  isTourActive: boolean;
  shareDone: boolean;
  dismissed: ReadonlySet<HintId>;
};

export type HintDefinition = {
  id: HintId;
  target: GuidanceTarget;
  /** Lower number = higher priority when multiple are eligible. */
  priority: number;
  isEligible: (ctx: HintEligibilityContext) => boolean;
};

function pathIs(pathname: string, root: string): boolean {
  const clean = canonicalizeShellPathname(pathname);
  return clean === root || clean.startsWith(`${root}/`);
}

/** Products list (not setup wizard). Visiting this satisfies nav-products discovery. */
export function onProductsList(pathname: string): boolean {
  const clean = canonicalizeShellPathname(pathname);
  if (clean === shellPaths.moreProductsSetup) return false;
  if (clean.startsWith(`${shellPaths.moreProductsSetup}/`)) return false;
  return pathIs(pathname, shellPaths.moreProducts);
}

function onReputation(pathname: string): boolean {
  return pathIs(pathname, shellPaths.reputationRoot);
}

/** Profiles list/home only — not edit / link-card (targets unmount there). */
function onProfilesHome(pathname: string): boolean {
  const clean = canonicalizeShellPathname(pathname);
  return clean === shellPaths.profiles;
}

function onLeadsRoot(pathname: string): boolean {
  const clean = canonicalizeShellPathname(pathname);
  return clean === shellPaths.leads;
}

/** Locations index — not create/detail (empty CTA lives on the list). */
function onLocationsHome(pathname: string): boolean {
  const clean = canonicalizeShellPathname(pathname);
  return clean === shellPaths.moreWorkspaceLocations;
}

/** People members home — not nested team routes if targets are list-only. */
function onPeopleHome(pathname: string): boolean {
  const clean = canonicalizeShellPathname(pathname);
  return (
    clean === shellPaths.morePeople ||
    clean.startsWith(`${shellPaths.morePeople}?`)
  );
}

/**
 * Catalog order = conflict priority (first match wins among eligible).
 * Route-local activation hints beat global discovery beacons.
 */
export const HINT_CATALOG: readonly HintDefinition[] = [
  {
    id: HINT_ID.profilesEmpty,
    target: GUIDANCE_TARGET.profilesEmpty,
    priority: 10,
    isEligible: (ctx) =>
      onProfilesHome(ctx.pathname) &&
      ctx.domain.isDomainReady &&
      !ctx.domain.hasProfile,
  },
  {
    id: HINT_ID.profileLinkCard,
    target: GUIDANCE_TARGET.profileLinkCard,
    priority: 20,
    isEligible: (ctx) =>
      onProfilesHome(ctx.pathname) &&
      ctx.domain.isDomainReady &&
      ctx.domain.hasProfile &&
      !ctx.domain.hasCard,
  },
  {
    id: HINT_ID.shareProfile,
    target: GUIDANCE_TARGET.shareProfile,
    priority: 30,
    isEligible: (ctx) =>
      onProfilesHome(ctx.pathname) &&
      ctx.domain.isDomainReady &&
      ctx.domain.hasProfile &&
      !ctx.shareDone,
  },
  {
    id: HINT_ID.productsEmpty,
    target: GUIDANCE_TARGET.productsEmpty,
    priority: 40,
    isEligible: (ctx) =>
      onProductsList(ctx.pathname) &&
      ctx.domain.isDomainReady &&
      ctx.domain.canManageProducts &&
      !ctx.domain.hasProduct,
  },
  {
    id: HINT_ID.liveScan,
    target: GUIDANCE_TARGET.liveScan,
    priority: 50,
    isEligible: (ctx) =>
      onProductsList(ctx.pathname) &&
      ctx.domain.isDomainReady &&
      ctx.domain.hasProduct,
  },
  {
    id: HINT_ID.locationsEmpty,
    target: GUIDANCE_TARGET.locationsEmpty,
    priority: 60,
    isEligible: (ctx) =>
      onLocationsHome(ctx.pathname) &&
      ctx.domain.isDomainReady &&
      ctx.domain.canManageLocations &&
      !ctx.domain.hasLocation,
  },
  {
    id: HINT_ID.leadsInbox,
    target: GUIDANCE_TARGET.leadsInbox,
    priority: 70,
    isEligible: (ctx) => onLeadsRoot(ctx.pathname),
  },
  {
    id: HINT_ID.peopleInvite,
    target: GUIDANCE_TARGET.peopleInvite,
    priority: 80,
    isEligible: (ctx) =>
      onPeopleHome(ctx.pathname) &&
      ctx.domain.isDomainReady &&
      ctx.domain.canInviteMembers &&
      !ctx.domain.hasTeam,
  },
  {
    id: HINT_ID.reputationSetup,
    target: GUIDANCE_TARGET.reputationSetup,
    priority: 90,
    isEligible: (ctx) =>
      onReputation(ctx.pathname) &&
      ctx.domain.isDomainReady &&
      ctx.domain.canSetupReputation &&
      !ctx.domain.hasGoogleLinked,
  },
  {
    id: HINT_ID.navProducts,
    target: GUIDANCE_TARGET.navProducts,
    priority: 100,
    // Discovery only — hide once they have a product or visit Products (auto-dismiss).
    isEligible: (ctx) =>
      ctx.domain.isDomainReady &&
      ctx.domain.canManageProducts &&
      !ctx.domain.hasProduct &&
      !onProductsList(ctx.pathname),
  },
];

export function pickActiveHint(
  ctx: HintEligibilityContext,
): HintDefinition | null {
  if (ctx.isTourActive) return null;

  const eligible = HINT_CATALOG.filter(
    (hint) => !ctx.dismissed.has(hint.id) && hint.isEligible(ctx),
  );
  if (eligible.length === 0) return null;

  return eligible.reduce((best, next) =>
    next.priority < best.priority ? next : best,
  );
}
